import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, message, Popconfirm, Tag, Space } from 'antd';
import { PlusOutlined, DeleteOutlined, CopyOutlined } from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';

const KeyManagement = () => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [form] = Form.useForm();

  useEffect(() => {
    fetchKeys();
  }, [pagination.current]);

  const fetchKeys = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`https://keyapi.dva.ink/api/admin/keys`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page: pagination.current,
          limit: pagination.pageSize
        }
      });

      setKeys(response.data.keys);
      setPagination(prev => ({
        ...prev,
        total: response.data.total
      }));
    } catch (error) {
      message.error('获取卡密列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateKeys = async (values) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('https://keyapi.dva.ink/api/admin/generate-keys', values, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        message.success(`成功生成 ${values.count} 个卡密`);
        setModalVisible(false);
        form.resetFields();
        fetchKeys();
      }
    } catch (error) {
      message.error(error.response?.data?.error || '生成卡密失败');
    }
  };

  const handleDeleteKey = async (id, force = false) => {
    try {
      const url = force ? `/api/admin/keys/${id}?force=true` : `/api/admin/keys/${id}`;
      const response = await axios.delete(`https://keyapi.dva.ink/api/admin/keys/${id}${force ? '?force=true' : ''}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      message.success(response.data.message || '删除成功');
      fetchKeys();
    } catch (error) {
      message.error('删除失败: ' + (error.response?.data?.error || error.message));
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    message.success('已复制到剪贴板');
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '卡密',
      dataIndex: 'key_code',
      key: 'key_code',
      render: (text) => (
        <Space>
          <code style={{ fontSize: '14px', fontWeight: 'bold' }}>{text}</code>
          <Button 
            type="text" 
            icon={<CopyOutlined />} 
            size="small"
            onClick={() => copyToClipboard(text)}
          />
        </Space>
      )
    },
    {
      title: '有效天数',
      dataIndex: 'valid_days',
      key: 'valid_days',
      width: 100,
      render: (days) => `${days} 天`
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const statusMap = {
          unused: { color: 'blue', text: '未使用' },
          used: { color: 'green', text: '已使用' },
          expired: { color: 'red', text: '已过期' }
        };
        const config = statusMap[status] || { color: 'default', text: status };
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (text) => moment(text).format('YYYY-MM-DD HH:mm:ss')
    },
    {
      title: '使用时间',
      dataIndex: 'used_at',
      key: 'used_at',
      width: 180,
      render: (text) => text ? moment(text).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    {
      title: '使用设备',
      dataIndex: 'used_by',
      key: 'used_by',
      render: (text) => text ? <code>{text.substring(0, 8)}...</code> : '-'
    },
    {
      title: 'IP地址',
      dataIndex: 'ip_address',
      key: 'ip_address',
      render: (text) => text || '-'
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Space>
          {record.status === 'used' ? (
            <Popconfirm
              title="强制撤回卡密"
              description="撤回后相关设备的激活将立即失效，确定要继续吗？"
              onConfirm={() => handleDeleteKey(record.id, true)}
              okText="确定撤回"
              cancelText="取消"
              okButtonProps={{ danger: true }}
            >
              <Button 
                type="text" 
                danger 
                size="small"
                icon={<DeleteOutlined />}
              >
                撤回
              </Button>
            </Popconfirm>
          ) : (
            <Popconfirm
              title="确定要删除这个卡密吗？"
              onConfirm={() => handleDeleteKey(record.id, false)}
              okText="确定"
              cancelText="取消"
            >
              <Button 
                type="text" 
                danger 
                size="small"
                icon={<DeleteOutlined />}
              >
                删除
              </Button>
            </Popconfirm>
          )}
        </Space>
      )
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2>卡密管理</h2>
        <Button 
          type="primary" 
          icon={<PlusOutlined />}
          onClick={() => setModalVisible(true)}
        >
          生成卡密
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={keys}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          onChange: (page, pageSize) => {
            setPagination(prev => ({ ...prev, current: page, pageSize }));
          }
        }}
        scroll={{ x: 1200 }}
      />

      <Modal
        title="生成卡密"
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleGenerateKeys}
          initialValues={{ count: 1, validDays: 30 }}
        >
          <Form.Item
            name="count"
            label="生成数量"
            rules={[
              { required: true, message: '请输入生成数量' },
              { type: 'number', min: 1, max: 100, message: '数量必须在1-100之间' }
            ]}
          >
            <InputNumber min={1} max={100} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="validDays"
            label="有效天数"
            rules={[
              { required: true, message: '请输入有效天数' },
              { type: 'number', min: 1, max: 3650, message: '天数必须在1-3650之间' }
            ]}
          >
            <InputNumber min={1} max={3650} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                生成
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default KeyManagement;
